import React, { useContext, useState, useEffect, useRef } from 'react';
import { PrismInputText, PrismCard } from '@prism-ui/react';
import SaveAndStepButton from '../SaveAndStepButton.jsx';
import './ApplicantForm.css';
import EducationStep from '../EducationStep';
import CareerStep from '../CareerStep';
import ERGFormGroup from './ERGFormGroup';
import ApplicationHeader from '../ApplicationHeader';
import { useDebounce } from '../../../hooks/useDebounce';
import axios from 'axios';
import {
  faChevronRight,
  faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import ApplicationWindowContext from '../ApplicationContainer/ApplicationWindowContext';

const ApplicantProfileForm = ({
  currentUser,
  saveApplication,
  application: propApplication,
  applicantTypeId,
  educationEntries,
  careerEntries,
  handleDeleteCareer,
  handleDeleteEducation,
  createCareer,
  createEducation,
  updateCareer,
  updateEducation,
  jumpToStep,
}) => {
  const thisPageStep = 3;
  const { chapter_id: contextChapterId } = useContext(ApplicationWindowContext);

  const yearsSince = (hire_date) => {
    let currentDate = new Date();
    let startDate = hire_date ? new Date(hire_date) : new Date();
    let yearsMs = Math.abs(currentDate - startDate);
    const msPerYear = 31556952000;
    return (yearsMs / msPerYear).toFixed(2);
  };

  // Helper function to ensure string values
  const safeStringValue = (value) => {
    return value !== null && value !== undefined ? String(value) : '';
  };

  const [applicationState, setApplicationState] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [azureLocation, setAzureLocation] = useState('');
  const debouncedAppState = useDebounce(applicationState, 800);
  const isSavingRef = useRef(false);

  // Get Azure location from currentUser
  useEffect(() => {
    if (currentUser?.User?.office_location) {
      setAzureLocation(currentUser.User.office_location);
    }
  }, [currentUser]);

  // Initialize or create application on component mount
  useEffect(() => {
    const initializeApplication = async () => {
      console.log('Initializing application...');
      console.log('propApplication:', propApplication);

      if (propApplication && propApplication.id) {
        // Use existing application
        console.log('Using existing application with ID:', propApplication.id);
        // Backfill azure_office_location if it was never saved (older drafts)
        const azureOffice = propApplication.azure_office_location || currentUser?.User?.office_location || '';
        setApplicationState({
          ...propApplication,
          azure_office_location: safeStringValue(azureOffice),
        });
      } else {
        // Create new application
        console.log('Creating new application...');
        const newApplication = {
          user_id: currentUser.User.id,
          department: safeStringValue(currentUser.User.department),
          manager: safeStringValue(currentUser.User.manager_email),
          hire_date: currentUser.User.hire_date,
          title: safeStringValue(currentUser.User.job_title),
          erg: '',
          years_with_company: currentUser.User.hire_date
            ? yearsSince(currentUser.User.hire_date)
            : 0,
          location: safeStringValue(currentUser.User.office_location),
          azure_office_location: safeStringValue(currentUser.User.office_location),
          location_comments: '',
          chapter_id: contextChapterId,
          applicant_type_id: applicantTypeId,
          year: new Date().getFullYear(),
          active: true,
          status_id: 1, // pending
        };

        try {
          const savedApplication = await saveApplication(newApplication);
          console.log('Application created successfully:', savedApplication);
          setApplicationState(savedApplication);
        } catch (error) {
          console.error('Error creating application:', error);
        }
      }

      setIsInitializing(false);
      setHasChanges(false);
    };

    initializeApplication();
  }, [contextChapterId, applicantTypeId]); // Don't include propApplication to avoid loops

  // Update local state when propApplication changes (after saves)
  useEffect(() => {
    if (propApplication && propApplication.id && !isInitializing) {
      console.log('Updating application state from props:', propApplication);
      setApplicationState(propApplication);
      setHasChanges(false);
    }
  }, [propApplication, isInitializing]);

  const handleSubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }

    console.log('Saving application state:', applicationState);
    const savedApplication = await saveApplication(applicationState);
    console.log('Application saved:', savedApplication);
    setApplicationState(savedApplication);
    setHasChanges(false);
  };

  const handlePrismInput = (name) => (e) => {
    const value = e.detail?.value ?? e.detail;
    setHasChanges(true);
    setApplicationState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleInputChange = (event) => {
    const name = event?.target?.name;
    const value = event?.target?.value;
    setHasChanges(true);
    setApplicationState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Auto-save when form state settles after changes
  useEffect(() => {
    if (!hasChanges || isInitializing || !debouncedAppState?.id || isSavingRef.current) return;
    isSavingRef.current = true;
    saveApplication(debouncedAppState)
      .then((saved) => {
        if (saved) setApplicationState(saved);
        setHasChanges(false);
      })
      .finally(() => { isSavingRef.current = false; });
  }, [debouncedAppState]);

  // Wrapper functions to ensure application has ID before calling create functions
  const handleCreateCareer = (careerData) => {
    console.log('handleCreateCareer called');
    console.log('Current applicationState:', applicationState);

    if (!applicationState || !applicationState.id) {
      console.error('Cannot create career: Application ID missing');
      alert(
        'Please save your profile information first before adding career entries.'
      );
      return;
    }

    console.log(
      'Calling createCareer with application ID:',
      applicationState.id
    );
    createCareer(careerData);
  };

  const handleCreateEducation = (educationData) => {
    console.log('handleCreateEducation called');
    console.log('Current applicationState:', applicationState);

    if (!applicationState || !applicationState.id) {
      console.error('Cannot create education: Application ID missing');
      alert(
        'Please save your profile information first before adding education entries.'
      );
      return;
    }

    console.log(
      'Calling createEducation with application ID:',
      applicationState.id
    );
    createEducation(educationData);
  };

  const onContinue = () => {
    jumpToStep(thisPageStep + 1);
  };

  const onBack = () => {
    jumpToStep(2);
  };

  const maxYearsAtComcast = new Date().getFullYear() - 1963;

  // Show loading state while initializing
  if (isInitializing || !applicationState) {
    return (
      <div className="clr:text-center">
        <div>Loading profile...</div>
      </div>
    );
  }

  return (
    <div>
      <ApplicationHeader
        heading="Your Profile"
        message="Confirm your details below. Education and career history are at the bottom of the page."
      />

      <PrismCard display="static" style={{ marginBottom: '1.5rem' }}>
        <h2 className="app-section-heading">Work Information</h2>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 45%', minWidth: '250px' }}>
            <PrismInputText
              style={{ '--prism-font-caption-base-size': '0.95rem' }}
              label="Title"
              name="title"
              value={safeStringValue(applicationState.title)}
              required
              onPrismChange={handlePrismInput('title')}
            />
          </div>
          <div style={{ flex: '1 1 45%', minWidth: '250px' }}>
            <PrismInputText
              style={{ '--prism-font-caption-base-size': '0.95rem' }}
              label="Department"
              name="department"
              value={safeStringValue(applicationState.department)}
              required
              onPrismChange={handlePrismInput('department')}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <div style={{ flex: '1 1 45%', minWidth: '250px' }}>
            <PrismInputText
              style={{ '--prism-font-caption-base-size': '0.95rem' }}
              label="Manager Email"
              name="manager"
              value={safeStringValue(applicationState.manager)}
              required
              onPrismChange={handlePrismInput('manager')}
            />
          </div>
          <div style={{ flex: '1 1 45%', minWidth: '250px' }}>
            <PrismInputText
              style={{ '--prism-font-caption-base-size': '0.95rem' }}
              label="Tenure"
              name="years_with_company"
              type="number"
              value={String(applicationState.years_with_company || '')}
              required
              disabled={Boolean(currentUser?.User?.hire_date)}
              onPrismChange={handlePrismInput('years_with_company')}
            />
          </div>
        </div>
      </PrismCard>

      <PrismCard display="static" style={{ marginBottom: '1.5rem' }}>
        <h2 className="app-section-heading">Location</h2>
        <div style={{ marginBottom: '1rem' }}>
          <PrismInputText
            style={{ '--prism-font-caption-base-size': '0.95rem' }}
            label={`Azure indicates that your current office location is: ${applicationState.azure_office_location || azureLocation || ''}`}
            name="azure_office_location"
            value={safeStringValue(applicationState.azure_office_location || azureLocation)}
            required
            readonly
          />
        </div>
        <div>
          <PrismInputText
            style={{ '--prism-font-caption-base-size': '0.95rem' }}
            label="If this location is not correct, please write in your declared office location:"
            name="location_comments"
            value={safeStringValue(applicationState.location_comments)}
            required
            onPrismChange={handlePrismInput('location_comments')}
          />
        </div>
      </PrismCard>

      <PrismCard display="static" style={{ marginBottom: '1.5rem' }}>
        <h2 className="app-section-heading">Employee Resource Groups</h2>
        <ERGFormGroup
          id="formGridERG"
          value={safeStringValue(applicationState.erg)}
          name="erg"
          onChange={handleInputChange}
        />
      </PrismCard>

      {/* Only show these sections if application has an ID */}
      {applicationState && applicationState.id ? (
        <>
          <PrismCard display="static" style={{ marginBottom: '1.5rem' }}>
            <h2 className="app-section-heading">Career History</h2>
            <CareerStep
              createCareer={handleCreateCareer}
              application={applicationState}
              careerEntries={careerEntries}
              handleDeleteCareer={handleDeleteCareer}
              updateCareer={updateCareer}
            />
          </PrismCard>

          <PrismCard display="static" style={{ marginBottom: '1.5rem' }}>
            <h2 className="app-section-heading">Education</h2>
            <EducationStep
              createEducation={handleCreateEducation}
              application={applicationState}
              educationEntries={educationEntries}
              handleDeleteEducation={handleDeleteEducation}
              updateEducation={updateEducation}
            />
          </PrismCard>
        </>
      ) : (
        <PrismCard display="static" style={{ marginBottom: '1.5rem', textAlign: 'center', color: 'var(--color-text-faded)' }}>
          <p>
            Save your profile information to add education and career entries.
          </p>
        </PrismCard>
      )}

      <form onSubmit={handleSubmit}>
        <hr style={{ borderColor: 'var(--color-border)', margin: '2rem 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <SaveAndStepButton
            buttonText="Back"
            icon={faChevronLeft}
            iconPosition="left"
            onSave={handleSubmit}
            onSuccessFunction={onBack}
          />
          <SaveAndStepButton
            buttonText="Continue"
            icon={faChevronRight}
            iconPosition="right"
            componentsToCheck={[
              'title',
              'department',
              'manager',
              'location',
              'years_with_company',
            ]}
            onSave={handleSubmit}
            onSuccessFunction={onContinue}
          />
        </div>
      </form>
    </div>
  );
};

export default ApplicantProfileForm;
